import { useMemo, useState, type ReactNode } from 'react';
import { useWarehouseLookup } from '../../hooks/useWarehouseLookup';
import { ExportDialog } from '../../components/ExportDialog';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  Eye, Download, Minus, ArrowRight, Package, PackageCheck, Boxes, Layers, IndianRupee,
  Warehouse, Store, FileText, History, Info,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useUrlString } from '../../hooks/useUrlState';
import {
  Card, Tag, Typography, Select, Table, Steps, Button, Modal, EntityAvatar,
  StatCard, SectionHead, SECTION_TINT, cn, CodeChip, entityHue, message,
} from '../../ui';
import { CopyValue } from '../../components/CopyValue';
import { RoleBadge } from '../../components/RoleBadge';
import { UserLink } from '../../components/EntityLinks';
import { productImages } from '../../lib/imageUrls';
import { AttachmentCell } from '../../components/AttachmentPreview';
import type { TagTone, StepItem } from '../../ui';
import { request } from '../../api/client';
import { requestAllPages } from '../../api/allPages';
import { useServerSort } from '../../hooks/useServerSort';
import { TwDataTable as DataTable } from '../../components/TwDataTable';
import type { DataTableColumn } from '../../components/TwDataTable';
import { formatIst, formatIstDate } from '../../lib/time';
import { variantText, normalizeMeasureLabel } from '../../lib/format';
import { warehouseOption } from '../../lib/warehouseLabel';

const inr = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

/**
 * `useWarehouseLookup().labelOf` hands back one string, "WH-PUNE · Pune Warehouse". Joined by a dot
 * the two halves read as a single run-on line and the CODE — the half people say out loud and scan
 * for — dissolves into it. Split so the code can be a chip in its own warehouse's colour.
 *
 * A vendor name has no dot form, so it comes back as ['', name] and the caller draws it plainly.
 */
const splitWhLabel = (label: string): [string, string] => {
  const i = String(label ?? '').indexOf(' · ');
  return i > 0 ? [label.slice(0, i), label.slice(i + 3)] : ['', String(label ?? '')];
};

/** A hue worn as a surface — wash, hairline ring, glyph. Inline styles need real colour values;
 *  Tailwind only emits the theme vars a CLASS references. */
const hueSkin = (hue: string, wash = 14, ring = 38) => ({
  background: `color-mix(in srgb, ${hue} ${wash}%, transparent)`,
  boxShadow: `inset 0 0 0 1px color-mix(in srgb, ${hue} ${ring}%, transparent)`,
  color: hue,
});

// A receipt's dates were printed the way the database stores them, "2026-01-01". The rest of the
// app writes "01 Jan 2026", and this dialog is the human-readable face of the record.
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtDay = (v: any) => {
  const d = v ? String(v).slice(0, 10) : '';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return null;
  const [y, m, dd] = d.split('-');
  return `${dd} ${MONTHS[Number(m) - 1]} ${y}`;
};
const isPast = (v: any) => { const d = fmtDay(v) && String(v).slice(0, 10); return !!d && d < new Date().toISOString().slice(0, 10); };

/**
 * The one way a variant is named on this page — the Product filter, the detail drawer's line table and
 * the printed GRN all read from here, so a pack is spelled identically in all three.
 *
 * The pack comes from `packSize` + `uomCode` through the shared `variantText` ("500 ml", "1 L"), NOT
 * from the raw `variantLabel` column. That column is hand-typed and disagrees with itself and with
 * every other screen — it held "0.5L" and "0.25L" here while the products page, the order screens and
 * the catalogue all said "500 ml" and "250 ml".
 */
const productText = (p: any, fallback?: any) => {
  if (!p) return String(fallback ?? '');
  const name = String(p.name ?? '');
  const pack = variantText(p.packSize, p.uomCode, p.variantLabel);
  // Some product NAMES already carry their pack ("Bioshakti 25 Kg", "FoliaBoost 1 Litre", "EcoFungi
  // 500 ml"), so appending the derived pack printed the size twice — "Bioshakti 25 Kg 25 kg". The
  // comparison has to be unit-aware: "1 Litre" and "1 L" are the same size and never match literally.
  const q = normalizeMeasureLabel(pack);
  const saysPackAlready = !!q && normalizeMeasureLabel(name).endsWith(q);
  return [name, saysPackAlready ? '' : pack].filter(Boolean).join(' ') || p.sku || String(fallback ?? '');
};

const sourceTag = (v: string) => (v === 'TRANSFER'
  ? <Tag tone="cyan">Transfer</Tag>
  : v === 'PRODUCTION'
    ? <Tag tone="blue">Production (legacy)</Tag>
    : <Tag tone="neutral">Purchase</Tag>);
const grnStatusTag = (v: string) => <Tag tone={v === 'POSTED' ? 'green' : v === 'CANCELLED' ? 'red' : 'neutral'}>{v}</Tag>;

const TR_STATUS_COLOR: Record<string, TagTone> = {
  RECEIVED: 'green', PARTIAL: 'gold', IN_TRANSIT: 'blue', PREPARED: 'gold',
  SOURCE_ACK: 'cyan', REQUESTED: 'neutral', CANCELLED: 'red', REJECTED: 'red',
};

// Open a print-ready GRN document in a new tab; the browser's print dialog can save it as PDF.
function printGrn(grn: any, whLabel: (id: any) => any, productMap: Map<string, any>, win?: Window | null) {
  const esc = (s: any) => String(s ?? '').replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c] as string));
  const origin = window.location.origin;
  const from = grn.fromWarehouseId ? whLabel(grn.fromWarehouseId) : (grn.vendorName ?? '—');
  const to = whLabel(grn.warehouseId);
  const lines = (grn.lines ?? []).map((l: any) => `
    <tr>
      <td>${esc(productText(productMap.get(String(l.productId)), l.productId))}</td>
      <td>${esc(l.batchNo)}</td><td>${esc(l.mfgDate ?? '')}</td><td>${esc(l.expDate ?? '')}</td>
      <td class="r">${Number(l.qty)}</td><td class="r">${esc(inr(l.value))}</td>
    </tr>`).join('');
  const timeline = grn.transfer ? `
    <div class="sec"><div class="sec-h">Status timeline · ${esc(grn.transfer.transferNo)} · ${esc(String(grn.transfer.status).replace(/_/g, ' '))}</div>
      <ol class="tl">${(grn.transfer.timeline ?? []).map((t: any) => `<li><b>${esc(t.label)}</b> — ${esc(formatIst(t.at))}${t.by ? ` · ${esc(t.by)}` : ''}</li>`).join('')}</ol></div>` : '';
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(grn.grnNo)}</title>
    <style>
      *{font-family:Arial,Helvetica,sans-serif;-webkit-print-color-adjust:exact;}
      body{margin:28px;color:#222;} h1{font-size:20px;margin:6px 0 2px;}
      .brand{color:#1F8A4C;font-weight:800;letter-spacing:.5px;} .muted{color:#777;font-size:12px;}
      .grid{display:grid;grid-template-columns:1fr 1fr;gap:6px 24px;margin:16px 0;font-size:13px;}
      .grid span{color:#777;display:inline-block;width:120px;}
      table{width:100%;border-collapse:collapse;margin-top:8px;font-size:12px;}
      th,td{border:1px solid #ddd;padding:6px 8px;text-align:left;} th{background:#f5f5f5;}
      .r{text-align:right;} .tot{font-weight:bold;}
      .sec{margin-top:18px;} .sec-h{font-weight:bold;font-size:13px;margin-bottom:6px;}
      .tl{margin:0;padding-left:18px;font-size:12px;line-height:1.7;}
    </style></head>
    <body onload="window.print()">
      <img src="${origin}/logo.png" alt="Green Bay Biotech International" style="height:64px;display:block;margin-bottom:6px;" />
      <h1>Goods Receipt Note</h1><div class="muted">${esc(grn.grnNo)}</div>
      <div class="grid">
        <div><span>Date</span>${esc(grn.grnDate)}</div><div><span>Status</span>${esc(grn.status)}</div>
        <div><span>From</span>${esc(from)}</div><div><span>To</span>${esc(to)}</div>
        <div><span>Source</span>${esc(grn.sourceType)}</div><div><span>Ref / Invoice</span>${esc(grn.vendorInvoiceNo ?? '—')}</div>
        <div><span>Value added</span><b>${esc(inr(grn.value))}</b></div>
      </div>
      ${timeline}
      <div class="sec"><div class="sec-h">Received items</div>
        <table><thead><tr><th>Product</th><th>Batch</th><th>Mfg</th><th>Exp</th><th class="r">Qty</th><th class="r">Value</th></tr></thead>
          <tbody>${lines}</tbody>
          <tfoot><tr><td colspan="5" class="r tot">Value added</td><td class="r tot">${esc(inr(grn.value))}</td></tr></tfoot>
        </table></div>
      <p class="muted" style="margin-top:24px;">Generated ${esc(formatIst(new Date().toISOString()))}</p>
    </body></html>`;
  // A caller that had to fetch the receipt first opens its tab on the click and hands it over here:
  // a window.open that lands after an await is treated as unrequested and is blocked.
  const w = win ?? window.open('', '_blank');
  if (!w) return;
  w.document.open();
  w.document.write(html);
  w.document.close();
}

export function GrnPage() {
  const [sourceType, setSourceType] = useUrlString<string | undefined>('sourceType', undefined);
  const [warehouseId, setWarehouseId] = useUrlString<string | undefined>('wh', undefined);
  const [detailRow, setDetailRow] = useState<any | null>(null);
  // Same gate as the page and the endpoint (GRN.VIEW). A receipt register carries purchase value, so it
  // stays staff-only via assertStaffOnly on the server as well.
  const [exportOpen, setExportOpen] = useState(false);

  // Picker offers active, in-scope warehouses; labels resolve through the master list too, so a GRN
  // booked into a since-deactivated warehouse still prints its name. See useWarehouseLookup.
  const { options: warehouses, labelOf: whLabel } = useWarehouseLookup();
  const { data: products } = useQuery({ queryKey: ['products', 'all'], queryFn: () => requestAllPages('/products') });
  const productList = useMemo(() => products?.data ?? [], [products]);

  const productMap = useMemo(() => new Map<string, any>(productList.map((p: any) => [String(p.id), p])), [productList]);

  /*
   * Download the receipt document straight from its row.
   *
   * The register row holds none of what that document prints — the received lines and the transfer's
   * timeline are only on `/grn/:id` — so the full receipt is fetched first. `fetchQuery` uses the same
   * cache key as the detail dialog, so a receipt already opened once prints without a second round trip.
   *
   * The tab is opened on the CLICK, before the await, because a pop-up that lands after one no longer
   * counts as user-requested and is blocked.
   */
  const qc = useQueryClient();
  const [printingId, setPrintingId] = useState<string | null>(null);
  async function downloadGrn(r: any) {
    const w = window.open('', '_blank');
    if (!w) { message.error('Allow pop-ups for this site to download a receipt.'); return; }
    w.document.write('<!doctype html><title>Preparing…</title><body style="margin:28px;font:14px Arial;color:#777">Preparing the goods receipt note…</body>');
    setPrintingId(String(r.id));
    try {
      const full = await qc.fetchQuery({ queryKey: ['grn', r.id], queryFn: () => request<any>({ url: `/grn/${r.id}` }) });
      printGrn(full ?? r, whLabel, productMap, w);
    } catch {
      w.close();
      message.error('Could not load this receipt to download.');
    } finally {
      setPrintingId(null);
    }
  }

  /*
   * There is deliberately NO product filter here.
   *
   * A GRN is a receipt DOCUMENT, and this register lists documents — GRN no, date, source, from/to,
   * value, status. Not one column names a product; they live on the lines inside, visible only once a
   * row is opened. So filtering by product answered a question the page cannot show the answer to: you
   * narrowed to a product, then had to open each receipt to see which line matched.
   *
   * It also offered every product master (38 here) when only 8 have ever appeared on a GRN, so most
   * choices could only ever return an empty table — including "AGN-2L", a retired SKU with no receipts
   * at all. To find the receipts for one product, use Batch trace or Stock history, which are built
   * around a product and show per-line movement.
   */

  // Server-side column sort (backend ORDER BY over the full result set, not just this page).
  const { sortState, onSortChange, sort } = useServerSort();

  const params = useMemo(() => {
    const p: Record<string, string> = {};
    if (sourceType) p.sourceType = sourceType;
    if (warehouseId) p.warehouseId = warehouseId;
    if (sort) p.sort = sort;
    return p;
  }, [sourceType, warehouseId, sort]);

  const { data, isLoading } = useQuery({
    queryKey: ['grns', params],
    queryFn: () => request<any[]>({ url: '/grn', params }),
    placeholderData: keepPreviousData,
  });

  /*
   * NO widths and NO `ellipsis` on this table — it runs on `autoFit` (see the DataTable below).
   *
   * The warehouse names were being cut to "WH-PU…" because `ellipsis` puts `max-w-0` on the cell, and
   * under the default AUTO layout that reads as "make this column as narrow as you can": From and To
   * collapsed while GRN No, Date and a usually-empty Ref / Invoice sat on acres of blank space. Pinning
   * widths on the others fixed the truncation but only moved the problem — all the slack then landed in
   * those two columns, leaving a canyon between a warehouse name and the next value.
   *
   * `autoFit` sizes every column from its own content and shares the leftover width across all of them
   * in proportion, so the spare space becomes an even gutter between columns instead of pooling in one.
   * Both `width` and `ellipsis` opt a column out of that, so neither belongs here. Status and Source are
   * CENTRED: they hold a short tag under a wider heading, which pinned to the left edge reads as a gap.
   */
  const columns: DataTableColumn<any>[] = [
    {
      key: 'grnNo', title: 'GRN No', dataIndex: 'grnNo', sortable: true, alwaysVisible: true,
      render: (v: string) => (
        // No plate. "Stock arriving is the one thing every row has in common" is precisely why the
        // glyph had to go: a mark that is identical on every row states the column header again
        // instead of telling one row from the next.
        <b className="block min-w-0 truncate">{v}</b>
      ),
    },
    // nowrap: an ISO date is one value, and letting it wrap printed "2026-09-" over "03".
    { key: 'grnDate', title: 'Date', dataIndex: 'grnDate', sortable: true, render: (v: any) => <span className="whitespace-nowrap text-muted">{v ?? '—'}</span> },
    { key: 'sourceType', title: 'Source', dataIndex: 'sourceType', sortable: true, align: 'center' as const, render: (v: string) => sourceTag(v) },
    // From: source warehouse for a transfer; for an external purchase, the vendor.
    {
      key: 'fromWarehouseId', title: 'From', dataIndex: 'fromWarehouseId',
      render: (v: any, r: any) => {
        const label = v ? whLabel(v) : (r.vendorName ?? null);
        if (!label) return <span className="text-subtle">—</span>;
        return (
          // Two glyphs (warehouse vs factory) is still a picture per row rather than a mark that
          // identifies the row; the source's own name is what tells them apart.
          <span className="block min-w-0 truncate">{label}</span>
        );
      },
    },
    // To: the receiving (destination) warehouse — always the GRN's own warehouse.
    // (Not server-sortable: the cell renders a joined warehouse name, not the raw id it would sort by.)
    {
      key: 'warehouseId', title: 'To', dataIndex: 'warehouseId',
      render: (v: any) => (
        <span className="flex min-w-0 items-center gap-2">
          <ArrowRight size={13} className="shrink-0 text-subtle" />
          <span className="min-w-0 truncate">{whLabel(v)}</span>
        </span>
      ),
    },
    // Value added is computed post-query (Σ line qty × dealer price) — not SQL-expressible here, so not sortable.
    { key: 'value', title: 'Value added', dataIndex: 'value', align: 'right' as const, render: (v: any) => inr(v) },
    { key: 'vendorInvoiceNo', title: 'Ref / Invoice', dataIndex: 'vendorInvoiceNo', align: 'center' as const, render: (v: any) => v ?? <span className="text-subtle">—</span> },
    { key: 'status', title: 'Status', dataIndex: 'status', sortable: true, align: 'center' as const, render: (v: string) => grnStatusTag(v) },
    { key: 'vendorName', title: 'Vendor / Source', dataIndex: 'vendorName', sortable: true, defaultVisible: false },
    { key: 'createdAt', title: 'Created', dataIndex: 'createdAt', sortable: true, defaultVisible: false, render: (v: string) => formatIst(v) },
    {
      key: 'actions', title: '', alwaysVisible: true, width: 88, align: 'center' as const, fixed: 'right' as const,
      render: (_: any, r: any) => (
        <div className="flex items-center justify-center gap-0.5">
          <Button
            variant="text" size="sm" icon={<Eye size={16} />} title="View full details" aria-label="View full details"
            onClick={(e) => { e.stopPropagation(); setDetailRow(r); }}
          />
          {/* Printing a receipt used to mean opening it first — the only Download button was inside the
              dialog. It is the same document either way; see downloadGrn. */}
          <Button
            variant="text" size="sm" icon={<Download size={16} />} loading={printingId === String(r.id)}
            title="Download receipt" aria-label="Download receipt"
            onClick={(e) => { e.stopPropagation(); void downloadGrn(r); }}
          />
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <Typography.Title level={4} style={{ margin: 0 }}>Goods Receipt Notes (GRN)</Typography.Title>
        <Button size="sm" className="ml-auto" icon={<Download size={16} />} onClick={() => setExportOpen(true)}>Export</Button>
      </div>
      <Typography.Paragraph type="secondary" style={{ marginTop: -8 }}>
        GRNs are generated automatically when an inter-warehouse transfer is received — they are not created manually.
      </Typography.Paragraph>
      <Card>
        <DataTable
          storageKey="grn-list"
          rowKey="id"
          autoFit
          loading={isLoading}
          dataSource={data ?? []}
          columns={columns}
          sortState={sortState}
          onSortChange={onSortChange}
          pagination={{ pageSize: 25, showSizeChanger: true }}
          /*
           * NO EXPANDED ROW. The chevron used to unroll the transfer timeline and the received lines
           * underneath the row — the same content the receipt dialog shows, only squeezed into the
           * register's width and pushing every later GRN down the page. Two ways to see one thing,
           * one of them worse, and the reader had to guess which the chevron would give them.
           *
           * The row click now opens the receipt, and that is the only route. TwDataTable adds
           * `cursor-pointer` once a row has an onClick, so the affordance follows the behaviour.
           */
          onRow={(r: any) => ({ onClick: () => setDetailRow(r) })}
          toolbarLeft={
            <>
              <Select
                placeholder="Source" className="w-37.5"
                value={sourceType} onChange={setSourceType}
                options={[{ value: 'TRANSFER', label: 'Transfer' }, { value: 'PURCHASE', label: 'Purchase' }, { value: 'PRODUCTION', label: 'Production (legacy)' }]}
              />
              <Select
                placeholder="Warehouse" className="w-50"
                value={warehouseId} onChange={setWarehouseId}
                options={(warehouses ?? []).map((w: any) => ({ value: String(w.id), ...warehouseOption(w) }))}
              />
            </>
          }
        />
      </Card>

      <GrnDetailModal row={detailRow} open={!!detailRow} onClose={() => setDetailRow(null)} productMap={productMap} whLabel={whLabel} />
      {/* No period picker: the GRN list filters by source and warehouse — it has no date range of its
          own, and offering one would filter nothing. */}
      <ExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        title="Export goods receipts"
        description="What came into each warehouse — from a vendor or another warehouse — with source, status and value."
        baseUrl="/grn/export"
        /* `sort` too: the column the reader sorted the register by is part of the view the
           file claims to be. Stripping it handed back a file in some other order. */
        baseParams={params}
        hidePeriod
      />
    </div>
  );
}

// Status timeline for a transfer-sourced GRN: From → To, current status, and the lifecycle stamps
// (requested → acknowledged → prepared → dispatched → received) with the time + who did each.
function TransferTimeline({ transfer, whLabel, hideRoute }: {
  transfer: any;
  whLabel: (id: any) => any;
  /** The detail dialog states the route once, in its own band. Suppresses the duplicate here. */
  hideRoute?: boolean;
}) {
  const navigate = useNavigate();
  const STAGES = [
    { key: 'REQUESTED', label: 'Requested' },
    { key: 'SOURCE_ACK', label: 'Source acknowledged' },
    { key: 'PREPARED', label: 'Prepared' },
    { key: 'IN_TRANSIT', label: 'Dispatched' },
    { key: 'RECEIVED', label: 'Received' },
  ];
  const byKey = new Map<string, any>((transfer.timeline ?? []).map((t: any) => [t.key, t]));
  const lastDone = STAGES.reduce((acc, s, i) => (byKey.has(s.key) ? i : acc), -1);
  return (
    <div className="mb-4">
      {/* Header: which transfer, where it moved, where it got to. Its own block so the long
          "WH-PUNE · Pune Warehouse → WH-BANGLORE · Banglore" can wrap without dragging the tags around. */}
      <div className={cn('mb-3 rounded-card border border-line bg-surface-2/50 px-3 py-2.5', hideRoute && 'hidden')}>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button" title="Open this transfer" className="cursor-pointer"
            onClick={() => navigate(`/inventory/master-stock?tab=transfers&q=${encodeURIComponent(transfer.transferNo)}`)}
          >
            <Tag tone="cyan">{transfer.transferNo} ↗</Tag>
          </button>
          <Tag tone={TR_STATUS_COLOR[transfer.status] ?? 'neutral'}>{String(transfer.status).replace(/_/g, ' ')}</Tag>
        </div>
        <div className="mt-1.5 text-sm text-content">
          <span className="font-medium">{whLabel(transfer.fromWarehouseId)}</span>
          <span className="mx-1.5 text-subtle">→</span>
          <span className="font-medium">{whLabel(transfer.toWarehouseId)}</span>
        </div>
      </div>
      {/*
        VERTICAL, not horizontal.
        Five stages side by side inside a drawer gave each one ~120px, so "Source acknowledged" and the
        timestamps wrapped to different heights — and because the horizontal row centres the circle
        against its label, every marker ended up at a different height. Stacked, each stage gets a full
        row: markers line up on one rail and the stamps read as the chronology they are.
      */}
      <Steps
        direction="vertical"
        size="small"
        items={STAGES.map((s, i) => {
          const t = byKey.get(s.key);
          // An unstamped stage means two different things, and showing both as "Pending" was wrong:
          // once a LATER stage has happened, this one is never going to — it was skipped. A transfer
          // the system raises for an order is created and dispatched in the same operation, so nobody
          // acknowledges or prepares it, and those stages hold no stamp on a fully received transfer.
          const skipped = !t && i < lastDone;
          return {
            title: s.label,
            // A stage that HAS a stamp is DONE — the last one included. Deriving this from `current`
            // alone marked the most recent stage "in progress", so a fully received transfer showed a
            // hollow "5" on Received instead of a tick.
            status: (t ? 'finish' : !skipped && i === lastDone + 1 ? 'process' : 'wait') as StepItem['status'],
            // A dash, not the step number: a skipped stage is not something still queued up.
            icon: skipped ? <Minus className="size-4" /> : undefined,
            /* WHO did it, named the way the rest of the app names a person: the name is a link to
               that staff member and their role is a coloured pill beside it. Five identical grey
               "by Super Admin" lines said who, but not what they were acting as, and led nowhere —
               and on a real transfer these five are usually five DIFFERENT people, which is the
               whole point of reading a timeline. RoleBadge/UserLink degrade to plain text on their
               own when the viewer lacks USER.VIEW or the actor is the Super Admin. */
            description: t
              ? (
                <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                  <span>{formatIst(t.at)}</span>
                  {t.by ? (
                    <>
                      <span className="text-subtle">· by</span>
                      <UserLink id={t.byId} role={t.byRole}>{t.by}</UserLink>
                      {t.byRole ? <RoleBadge role={t.byRole} /> : null}
                    </>
                  ) : null}
                </span>
              )
              : <span className="text-subtle">{skipped ? 'Not recorded' : 'Pending'}</span>,
          };
        })}
      />
      {STAGES.some((s, i) => !byKey.has(s.key) && i < lastDone) && (
        <p className="mt-2 text-xs text-subtle">
          Stages marked “not recorded” were skipped — a transfer raised automatically for an order is
          created and dispatched in one step, with no one to acknowledge or prepare it.
        </p>
      )}
    </div>
  );
}

// The received lines with their value + a "Value added" total — shared by the inline expand and the
// detail dialog. The dialog turns the total OFF: it carries the same figure in its KPI band, and one
// screen must never print one number twice.
function GrnLinesTable({ lines, value, loading, productMap, hideTotal }: {
  lines: any[]; value: any; loading?: boolean; productMap: Map<string, any>; hideTotal?: boolean;
}) {
  return (
    <Table
      size="small"
      loading={loading}
      pagination={false}
      rowKey="id"
      dataSource={lines}
      columns={[
        {
          // A receipt line is about a physical thing, and people recognise the pack long before they
          // read its name. The gallery image the catalogue already holds is that recognition.
          title: 'Product',
          render: (_: any, l: any) => {
            const p = productMap.get(String(l.productId));
            const label = productText(p, l.productId);
            const photos = productImages(p);
            return (
              <span className="flex min-w-0 items-center gap-2.5">
                {/*
                  The thumbnail OPENS. At 34px a pack shot is enough to tell two products apart and
                  nowhere near enough to check a label, a batch sticker or a damaged seal — which is
                  the reason someone looks at a receipt's photo at all. `AttachmentCell` is the app's
                  one way to do this (arrow keys walk a gallery, Esc closes, "Open original" escapes),
                  so this cell cannot drift from every other place a picture is previewed.

                  A product with no photo keeps the avatar, which is inert on purpose: a control that
                  opens nothing is worse than no control.
                */}
                <AttachmentCell
                  files={photos}
                  label={`${label} photo`}
                  size={34}
                  maxThumbs={1}
                  empty={<EntityAvatar name={label} square size={34} icon={<Package size={15} />} />}
                />
                <span className="min-w-0 truncate" title={label}>{label}</span>
              </span>
            );
          },
        },
        {
          // The batch is what gets pasted into a complaint, a recall note or a supplier message, and
          // it is long enough that selecting it by dragging is how a character goes missing.
          title: 'Batch',
          dataIndex: 'batchNo',
          render: (v: any) => (v ? <CopyValue value={v} label="Batch no"><CodeChip>{v}</CodeChip></CopyValue> : <span className="text-subtle">—</span>),
        },
        /*
         * The four short columns declare their width, and the two long ones do not.
         *
         * A column that declares nothing is floored at 150px when the table works out how narrow it
         * may go — six of those is a 900px minimum, which is wider than the dialog panel holding it,
         * so the receipt scrolled sideways and hid Qty and Value. A date is never 150px wide and a
         * quantity certainly is not; saying so leaves the product name and the batch number to share
         * everything that is left over, which is where the width is actually wanted.
         */
        { title: 'Mfg', dataIndex: 'mfgDate', width: 112, render: (v: any) => fmtDay(v) ?? <span className="text-subtle">—</span> },
        {
          // A lot received already past its date is the one thing on a receipt worth shouting about.
          title: 'Exp', dataIndex: 'expDate', width: 128,
          render: (v: any) => {
            const d = fmtDay(v);
            if (!d) return <span className="type-label text-warning-600 dark:text-harvest-300">none</span>;
            return <span className="whitespace-nowrap">{d}{isPast(v) ? <Tag tone="red" className="ml-1.5">past</Tag> : null}</span>;
          },
        },
        { title: 'Qty', dataIndex: 'qty', align: 'right' as const, width: 76, render: (v: any) => <span className="font-semibold tabular-nums text-content">{Number(v)}</span> },
        { title: 'Value', dataIndex: 'value', align: 'right' as const, width: 112, render: (v: any) => <span className="tabular-nums">{inr(v)}</span> },
      ]}
      footer={hideTotal ? undefined : () => (
        <div className="flex items-center justify-end gap-6 font-semibold text-content">
          <span>Value added</span>
          <span>{inr(value)}</span>
        </div>
      )}
    />
  );
}

/** One fact in the dialog's right-hand rail. Label sentence-case and subtle, value 14px/600 — the
 *  middle and bottom of the three levels a detail view has (SECTION / label / value). */
function GrnField({ label, children, className }: { label: string; children?: ReactNode; className?: string }) {
  return (
    <div className={className}>
      <dt className="type-caption text-subtle">{label}</dt>
      <dd className="mt-0.5 type-body-strong text-content">{children ?? <span className="font-normal text-subtle">—</span>}</dd>
    </div>
  );
}

/**
 * One end of the movement — a warehouse the stock left, or the vendor it was bought from.
 *
 * Both ends used to be the same grey tile over the same grey run-on string, so the one fact a
 * receipt is ABOUT ("from here, to there") looked like two copies of the same thing. Each end now
 * wears its warehouse's identity hue — the colour that warehouse carries in every picker, chip and
 * avatar app-wide — so the direction is legible before a word is read.
 */
function RouteEnd({ icon, cap, name }: { icon: ReactNode; cap: string; name: string }) {
  const [code, site] = splitWhLabel(name);
  const hue = code ? entityHue(code) : null;
  return (
    <div className="flex min-w-0 items-center gap-2.5">
      <span
        className={cn('grid size-9 shrink-0 place-items-center rounded-xl shadow-e1 [&>svg]:size-4', !hue && 'bg-surface text-muted')}
        style={hue ? hueSkin(hue, 16, 42) : undefined}
      >
        {icon}
      </span>
      <span className="min-w-0">
        <span className="block type-caption text-subtle">{cap}</span>
        <span className="flex min-w-0 items-center gap-1.5">
          {code && hue ? <CodeChip hue={hue}>{code}</CodeChip> : null}
          <span className="min-w-0 truncate type-body-strong text-content">{site}</span>
        </span>
      </span>
    </div>
  );
}

/**
 * The goods receipt, opened from the eye icon.
 *
 * A DIALOG RATHER THAN A DRAWER, and the change is not cosmetic. A GRN is a settled record — the
 * stock is already in the godown — so it is READ, never worked alongside the register behind it,
 * which is the one thing a side sheet is for. What the drawer cost was width: 720px of it, with the
 * receipt's six-column line table squeezed into a single narrow strip while half the screen sat
 * behind a dim mask. Centred, the same record gets 980px, and the items get the room to be read.
 *
 * IT ALSO SAYS MORE THAN THE DRAWER DID. The drawer was a bordered `Descriptions` grid — eleven
 * fields at one visual weight, uppercase labels competing with their own headings, and every figure
 * on it copied from the row you had already clicked. The dialog is built the way every other detail
 * view here is built (see `ui/SectionHead`): three visible levels — SECTION, label, value — and a
 * KPI band carrying the three things that were NOT on the row and that a receiving clerk actually
 * checks: how many lines came in, how many units that is, and how many batches they landed in.
 *
 * NOTHING IS PRINTED TWICE. The number, source and status live in the letterhead, so they are not
 * repeated as fields; the route lives in its own band, so the timeline below it drops the duplicate
 * copy it used to carry; the value total lives in the KPI band, so the line table's footer total is
 * turned off. A figure that appears twice on one screen invites the reader to check whether the two
 * agree, which is a job the screen should have done for them.
 */
function GrnDetailModal({ row, open, onClose, productMap, whLabel }: {
  row: any | null; open: boolean; onClose: () => void; productMap: Map<string, any>; whLabel: (id: any) => any;
}) {
  const id = row?.id;
  const { data, isLoading } = useQuery({
    queryKey: ['grn', id],
    queryFn: () => request<any>({ url: `/grn/${id}` }),
    enabled: !!id && open,
  });

  const lines: any[] = data?.lines ?? [];
  const value = data?.value ?? row?.value;
  // Counted off the lines rather than asked of the server: they are the same rows the table below
  // renders, so the band cannot drift from the list it captions.
  const units = lines.reduce((t, l) => t + Number(l.qty ?? 0), 0);
  const batches = new Set(lines.map((l) => String(l.batchNo ?? '').trim()).filter(Boolean)).size;
  const fromVendor = !row?.fromWarehouseId;

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title="Goods receipt note"
      description="What arrived, where it came from, and the batches it went into stock as."
      width={980}
      footer={
        <>
          <Button variant="default" onClick={onClose}>Close</Button>
          <Button variant="primary" icon={<Download size={16} />} disabled={!data}
            onClick={() => data && printGrn(data, whLabel, productMap)}>Download</Button>
        </>
      }
    >
      {row && (
        <>
          {/* Letterhead — which receipt this is and what state it is in. Tinted so it reads as the
              head of a record rather than as the first row of the fields under it. */}
          <div className="mb-4 flex flex-wrap items-center gap-3 rounded-card bg-linear-to-r from-brand-600/10 via-brand-500/5 to-transparent p-3 ring-1 ring-inset ring-brand-600/10">
            <span className="grid size-12 shrink-0 place-items-center rounded-2xl bg-brand-600 text-white shadow-e1 [&>svg]:size-6">
              <PackageCheck />
            </span>
            <div className="min-w-0">
              {/* The number is the thing people paste into a message or a ledger, so it is a chip
                  you press rather than text you drag a cursor across. */}
              <CopyValue value={row.grnNo} label="GRN no">
                <span className="type-section-title text-content">{row.grnNo}</span>
              </CopyValue>
              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                {sourceTag(row.sourceType)}
                {grnStatusTag(row.status)}
                {/* The date the GOODS arrived — the business fact. The keystroke that recorded it is a
                    different fact and lives under Paperwork; labelling both "received" would put two
                    dates on one screen that a reader has to guess between. */}
                <span className="type-caption text-muted">received {formatIstDate(row.grnDate)}</span>
              </div>
            </div>
          </div>

          {/* The movement — the one fact a receipt is ABOUT, and the only one worth a band of its
              own. Stated here once; the timeline below no longer repeats it. */}
          <div
            className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-3 rounded-card border border-line bg-surface-2/60 px-4 py-3"
            // The band itself fades from the source's colour to the destination's, so it reads as a
            // journey rather than as a grey strip holding two labels.
            style={(() => {
              const from = fromVendor ? '' : splitWhLabel(whLabel(row.fromWarehouseId))[0];
              const to = splitWhLabel(whLabel(row.warehouseId))[0];
              if (!to) return undefined;
              const a = from ? `color-mix(in srgb, ${entityHue(from)} 12%, transparent)` : 'transparent';
              return { backgroundImage: `linear-gradient(to right, ${a}, transparent 42%, color-mix(in srgb, ${entityHue(to)} 12%, transparent))` };
            })()}
          >
            <RouteEnd
              icon={fromVendor ? <Store /> : <Warehouse />}
              cap={fromVendor ? 'Bought from' : 'Sent from'}
              name={fromVendor ? (row.vendorName ?? '—') : whLabel(row.fromWarehouseId)}
            />
            {/* Hidden once the two ends stack: an arrow that has wrapped onto the end of the first
                line points at nothing. The captions carry the direction on their own. */}
            <ArrowRight className="hidden size-4 shrink-0 text-subtle sm:block" aria-hidden />
            <RouteEnd icon={<Warehouse />} cap="Received into" name={whLabel(row.warehouseId)} />
            {data?.transfer && (
              <span className="ml-auto flex flex-wrap items-center gap-1.5">
                <CopyValue value={data.transfer.transferNo} label="Transfer no">
                  <Tag tone="cyan">{data.transfer.transferNo}</Tag>
                </CopyValue>
                <Tag tone={TR_STATUS_COLOR[data.transfer.status] ?? 'neutral'}>
                  {String(data.transfer.status).replace(/_/g, ' ')}
                </Tag>
              </span>
            )}
          </div>

          {/* What actually came in. The value was the drawer's only figure; the other three are the
              ones a receiving clerk counts on the floor and could not check on this screen at all. */}
          <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StatCard tone="brand" icon={<IndianRupee />} label="Value added" value={inr(value)}
              hint="into this godown" />
            <StatCard tone="sky" icon={<Package />} label="Products" value={lines.length}
              hint="in this receipt" />
            <StatCard tone="gold" icon={<Boxes />} label="Units received" value={units.toLocaleString('en-IN')} />
            <StatCard tone="success" icon={<Layers />} label="Batches" value={batches}
              hint={batches ? 'traceable lots' : 'none recorded'} />
          </div>

          {/*
            THE ITEMS GET THE FULL WIDTH, and the two reference panels share the row beneath them.
            The first cut put the table in a column beside a 320px rail, which left it ~570px — and
            six columns, one of them a batch number like "AminOPF 250ml-OPENING-377", do not go into
            570px. The table scrolled sideways inside its own panel, so Mfg, Exp, Qty and Value were
            all off-screen: the drawer's exact failure, reproduced in a wider dialog. It also left a
            one-line receipt stretched to the height of a five-stage timeline, which is most of a
            screen of white space. Stacked, the lines get ~940px and every column is simply there.
          */}
          <section className="mb-4 min-w-0 rounded-card border border-line bg-surface p-4 shadow-e1">
            <SectionHead icon={<Package className="size-3.5" />} tint={SECTION_TINT.identity}
              hint={lines.length ? `${lines.length} ${lines.length === 1 ? 'product' : 'products'}` : undefined}>
              Received items
            </SectionHead>
            <GrnLinesTable lines={lines} value={value} loading={isLoading} productMap={productMap} hideTotal />
          </section>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="space-y-4">
              <section className="rounded-card border border-line bg-surface p-4 shadow-e1">
                <SectionHead icon={<FileText className="size-3.5" />} tint={SECTION_TINT.documents}>Paperwork</SectionHead>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-3.5">
                  <GrnField label="Ref / invoice">
                    {row.vendorInvoiceNo
                      ? <CopyValue value={row.vendorInvoiceNo} label="Invoice no">{row.vendorInvoiceNo}</CopyValue>
                      : undefined}
                  </GrnField>
                  <GrnField label="Entered on">{formatIst(row.createdAt)}</GrnField>
                  {row.vendorName && <GrnField label="Vendor" className="col-span-2">{row.vendorName}</GrnField>}
                  <GrnField label="Remarks" className="col-span-2">{data?.remarks}</GrnField>
                </dl>
              </section>

            </div>

            {data?.transfer ? (
              <section className="rounded-card border border-line bg-surface p-4 shadow-e1">
                <SectionHead icon={<History className="size-3.5" />} tint={SECTION_TINT.system}>Status timeline</SectionHead>
                <TransferTimeline transfer={data.transfer} whLabel={whLabel} hideRoute />
              </section>
            ) : (
              /* Said in words rather than left blank: an empty panel where a timeline sits on
                 other receipts reads as something that failed to load. */
              <p className="flex items-start gap-2 self-start rounded-card border border-line bg-surface-2/60 px-3 py-2.5 type-caption text-muted">
                <Info className="mt-px size-3.5 shrink-0" aria-hidden />
                A purchase receipt is entered in one step, so it has no transfer to trace.
              </p>
            )}
          </div>
        </>
      )}
    </Modal>
  );
}
